import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { formatDistanceToNowStrict } from 'date-fns'
import { ChevronLeftIcon } from '@heroicons/react/24/outline'
import { signalsAPI } from '../services/api'
import type { PortfolioHeat, ScanCorrelationWarning, ScanStatus } from '../types'
import Spinner from '../components/Spinner'
import Tooltip from '../components/Tooltip'
import TosGateModal from '../components/TosGateModal'
import Phase2ComparisonPanel from '../components/Phase2ComparisonPanel'
import ShadowFiltersPanel from '../components/ShadowFiltersPanel'
import { useAuthStore } from '../store/authStore'
import { SIGNALS_GLOSSARY as GLOSSARY } from '../config/glossary'
import { apiErrorMessage } from '../utils/apiError'
import { formatHeatPct } from '../utils/portfolioHeat'

type TradingMode = 'conservative' | 'growth'

// The scanner runs hourly; ScanStatus has no next-scan field, so the next run
// is derived from the last one plus that cadence.
const SCAN_INTERVAL_MS = 60 * 60 * 1000

const isTosRequired = (error: any) =>
  error?.response?.status === 403 && error?.response?.data?.detail?.error_code === 'tos_required'

/**
 * Settings → Engine: the admin home for the market scanner. The hourly scan
 * runs on its own, so this page leads with status and keeps the manual levers
 * (Run scan now / Analyze one symbol / Retrain) clearly marked as rarely
 * needed. Scan configuration lives in deployment settings and is shown here
 * read-only -- there is no endpoint to edit it, and the backend is off-limits.
 *
 * The route is gated to admins (live-trading gate) in App.tsx.
 */
const EngineSettings = () => {
  const [symbol, setSymbol] = useState('BTC/USDT')
  // 4h, not 1h: the backend suspends 1h (DISABLED_TIMEFRAMES).
  const [timeframe, setTimeframe] = useState('4h')
  const [mode, setMode] = useState<TradingMode>('conservative')
  const [analyzing, setAnalyzing] = useState(false)
  const [scanning, setScanning] = useState(false)
  const [retraining, setRetraining] = useState(false)
  const [circuitBreakerActive, setCircuitBreakerActive] = useState(false)
  const [circuitBreakerMessage, setCircuitBreakerMessage] = useState('')
  const [correlationWarnings, setCorrelationWarnings] = useState<ScanCorrelationWarning[]>([])
  const [showTosModal, setShowTosModal] = useState(false)
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)
  const [status, setStatus] = useState<ScanStatus | null>(null)
  const [heat, setHeat] = useState<PortfolioHeat | null>(null)
  const [configOpen, setConfigOpen] = useState(false)
  const isAdmin = useAuthStore((state) => state.user?.is_superuser)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    const load = async () => {
      const [s, h] = await Promise.allSettled([signalsAPI.scanStatus(), signalsAPI.portfolioHeat()])
      if (!mounted.current) return
      if (s.status === 'fulfilled') setStatus(s.value)
      if (h.status === 'fulfilled') setHeat(h.value)
    }
    load()
    return () => {
      mounted.current = false
    }
  }, [])

  const refreshStatus = () => {
    signalsAPI.scanStatus().then((s) => mounted.current && setStatus(s)).catch(() => {})
  }

  const handleTosAccepted = () => {
    setShowTosModal(false)
    if (pendingAction) {
      pendingAction()
      setPendingAction(null)
    }
  }

  const handleAnalyze = async () => {
    setAnalyzing(true)
    try {
      await signalsAPI.analyze(symbol, timeframe, mode)
      toast.success(`Analyzed ${symbol} on ${timeframe}. Any qualifying signal is now on the Signals page.`)
    } catch (error: any) {
      if (isTosRequired(error)) {
        setPendingAction(() => handleAnalyze)
        setShowTosModal(true)
      } else {
        toast.error(apiErrorMessage(error, 'No valid signal found'))
      }
    } finally {
      setAnalyzing(false)
    }
  }

  const handleScan = async () => {
    setScanning(true)
    setCorrelationWarnings([])
    try {
      const result = await signalsAPI.scan({ mode })
      setCircuitBreakerActive(false)
      if (result.signals_created === 0) {
        toast.success(`Scanned ${result.scanned} combos — no new signals met the threshold`)
      } else {
        toast.success(
          `Found ${result.signals_created} signal${result.signals_created > 1 ? 's' : ''} across ${result.scanned} scanned combos`
        )
      }
      if (result.correlation_warnings?.length) setCorrelationWarnings(result.correlation_warnings)
      refreshStatus()
    } catch (error: any) {
      if (isTosRequired(error)) {
        setPendingAction(() => handleScan)
        setShowTosModal(true)
      } else if (error?.response?.status === 503 || error?.response?.status === 429) {
        setCircuitBreakerActive(true)
        setCircuitBreakerMessage(
          apiErrorMessage(error, 'Signal generation paused: rolling P&L circuit breaker active.')
        )
      } else {
        toast.error(apiErrorMessage(error, 'Scan failed'))
      }
    } finally {
      setScanning(false)
    }
  }

  const handleRetrain = async () => {
    setRetraining(true)
    try {
      const result = await signalsAPI.retrain()
      if (result.status === 'trained') {
        toast.success(`ML model retrained on ${result.samples} signals!`)
      } else {
        toast.success(result.message)
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Retrain failed')
    } finally {
      setRetraining(false)
    }
  }

  const lastScan = status?.last_scan_at ? new Date(status.last_scan_at) : null
  const nextIn = lastScan
    ? Math.ceil((lastScan.getTime() + SCAN_INTERVAL_MS - Date.now()) / 60_000)
    : null

  const fieldClass =
    'px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent'
  const primaryButton =
    'px-4 py-2 bg-accent text-terminal-bg font-semibold rounded-md hover:bg-accent-hover disabled:opacity-50 flex items-center gap-2'

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {showTosModal && <TosGateModal onAccepted={handleTosAccepted} />}

      <div>
        <Link to="/settings" className="inline-flex items-center gap-1 text-sm text-accent hover:underline">
          <ChevronLeftIcon className="h-4 w-4" /> Settings
        </Link>
        <h1 className="mt-1 text-3xl font-bold text-gray-900">Engine</h1>
        <p className="mt-1 text-sm text-gray-500">The market scanner that produces the signals record.</p>
        <p className="mt-2 text-sm text-gray-500">
          Admin analysis:{' '}
          <Link to="/admin/exit-backtest" className="text-accent hover:underline">
            Exit-strategy backtest
          </Link>{' '}
          — replays closed signals under alternative exit rules. Read-only; not shown to subscribers.
        </p>
        <p className="mt-1 text-sm text-gray-500">
          <Link to="/admin/diagnostics" className="text-accent hover:underline">
            Engine diagnostics
          </Link>{' '}
          — scan outcomes by gate, failures, stall alerts and the open book at a glance. Read-only; no AI.
        </p>
      </div>

      {/* Status */}
      <section className="rounded-lg border border-gray-200 bg-white p-4" aria-label="Engine status">
        <div className="flex items-start gap-3">
          <span className="relative mt-1 flex h-2.5 w-2.5" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-500" />
          </span>
          <div className="text-sm">
            <p className="font-semibold text-gray-900">Automatic scanning is ON — scans every hour, no manual action.</p>
            <p className="mt-1 text-gray-500">
              {lastScan
                ? `Last scan ${formatDistanceToNowStrict(lastScan)} ago${
                    nextIn != null ? ` · ${nextIn <= 0 ? 'next scan due now' : `next in ${nextIn}m`}` : ''
                  }`
                : 'No completed scan has been recorded yet.'}
            </p>
          </div>
        </div>
      </section>

      {/* Manual actions */}
      <section className="space-y-3" aria-label="Manual engine actions">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Manual actions</h2>
          <p className="text-sm text-gray-500">Rarely needed — the hourly scan runs on its own.</p>
        </div>

        {circuitBreakerActive && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <span className="mt-0.5 text-amber-600">⛔</span>
            <div className="flex-1">
              <strong>Circuit breaker active</strong> — {circuitBreakerMessage}
            </div>
            <button
              onClick={() => setCircuitBreakerActive(false)}
              className="shrink-0 text-xs text-amber-600 underline hover:text-amber-900"
            >
              Dismiss
            </button>
          </div>
        )}

        {correlationWarnings.length > 0 && (
          <div className="space-y-1 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
            <strong>Correlation warnings</strong>
            {correlationWarnings.map((w, i) => (
              <p key={i} className="text-yellow-700">{w.message}</p>
            ))}
          </div>
        )}

        {/* Run scan now */}
        <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-4">
          <div>
            <label className="mb-1 block text-xs text-gray-500" htmlFor="scan-mode">Strategy</label>
            <select id="scan-mode" value={mode} onChange={(e) => setMode(e.target.value as TradingMode)} className={fieldClass}>
              <option value="conservative">Conservative</option>
              <option value="growth">Growth</option>
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleScan}
              disabled={scanning || circuitBreakerActive}
              className={primaryButton}
              title={circuitBreakerActive ? 'Scan paused: circuit breaker active' : 'Scan every tracked pair across every active timeframe'}
            >
              {scanning && <Spinner size="sm" className="border-terminal-bg border-t-transparent" />}
              {scanning ? 'Scanning…' : 'Run scan now'}
            </button>
            <Tooltip content={GLOSSARY.scanAll} label="Run scan help" />
          </div>
          <p className="w-full text-xs text-gray-400">
            <Tooltip content={GLOSSARY.scanScope}>Every tracked pair × active timeframe</Tooltip>
          </p>
        </div>

        {/* Analyze one symbol */}
        <div className="flex flex-wrap items-end gap-3 rounded-lg border border-gray-200 bg-white p-4">
          <div>
            <label className="mb-1 block text-xs text-gray-500" htmlFor="engine-symbol">Symbol</label>
            <input
              id="engine-symbol"
              type="text"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              placeholder="Symbol (e.g., BTC/USDT)"
              className={`${fieldClass} w-36`}
            />
          </div>
          <div>
            <div className="mb-1 flex items-center gap-1">
              <label className="text-xs text-gray-500" htmlFor="engine-timeframe">Timeframe</label>
              <Tooltip content={GLOSSARY.timeframe} label="timeframe" />
            </div>
            <select id="engine-timeframe" value={timeframe} onChange={(e) => setTimeframe(e.target.value)} className={fieldClass}>
              {['1h', '4h', '1d'].map((tf) => (
                <option key={tf} value={tf}>{tf}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-1.5">
            <button onClick={handleAnalyze} disabled={analyzing} className={primaryButton}>
              {analyzing && <Spinner size="sm" className="border-terminal-bg border-t-transparent" />}
              {analyzing ? 'Analyzing…' : 'Analyze one symbol'}
            </button>
            <Tooltip content={GLOSSARY.analyze} label="Analyze" />
          </div>
        </div>

        {/* Retrain */}
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleRetrain}
              disabled={retraining}
              className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              title="Retrain the Phase 2 ML model on closed signal outcomes"
            >
              {retraining && <Spinner size="sm" className="border-gray-400 border-t-transparent" />}
              {retraining ? 'Retraining…' : 'Retrain model'}
            </button>
            <Tooltip content={GLOSSARY.retrainMl} label="Retrain help" />
          </div>
          <p className="text-xs text-gray-400">Refreshes the model on the latest outcomes — it does not create an edge.</p>
        </div>
      </section>

      {/* Phase 2 model vs Phase 1 rule: admins only, whatever the route allows. */}
      {isAdmin && <Phase2ComparisonPanel />}

      {/* Two candidate rules, recorded and never applied: admins only. */}
      {isAdmin && <ShadowFiltersPanel />}

      {/* Read-only current configuration */}
      <section className="rounded-lg border border-gray-200 bg-white" aria-label="Current configuration">
        <button
          type="button"
          onClick={() => setConfigOpen((o) => !o)}
          aria-expanded={configOpen}
          className="flex w-full items-center justify-between px-4 py-3 text-left"
          data-testid="config-toggle"
        >
          <span className="font-semibold text-gray-800">Current configuration</span>
          <span className="text-sm text-gray-400">{configOpen ? '▲' : '▼'}</span>
        </button>
        {configOpen && (
          <div className="space-y-2 border-t border-gray-100 px-4 py-4 text-sm" data-testid="config-body">
            <div className="flex justify-between gap-4">
              <span className="text-gray-500">Tracked symbols</span>
              <span className="font-medium text-gray-800">
                {status ? `${status.symbols_scanned} pairs` : '—'}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-gray-500">
                <Tooltip content={GLOSSARY.portfolioHeat(heat ? formatHeatPct(heat.max_heat_pct, 0) : '')}>Portfolio-heat cap</Tooltip>
              </span>
              <span className="font-medium text-gray-800">
                {heat ? formatHeatPct(heat.max_heat_pct, 0) : '—'}
              </span>
            </div>
            <p className="pt-2 text-xs text-gray-400">Configured in deployment settings — not editable here.</p>
          </div>
        )}
      </section>
    </div>
  )
}

export default EngineSettings
